import * as Sentry from "@sentry/browser";
import { CardElement, useElements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { FormikErrors, FormikTouched, useFormik } from "formik";
import React, { Fragment, useCallback, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import { Container } from "../components";
import { Location, Space } from "../data";
import { PartnerOffering } from "../data/partner_offering";
import { useQueryString } from "../hooks";
import { FBT, useI18n } from "../lib/i18n/i18n_provider";
import {
  isCountryIsrael,
  isValidIsraeliTaxID,
} from "../lib/validation/israeli";
import { isEmail, isTenDigits } from "../lib/validation/string";
import {
  OfferingMessageType,
  Order,
  OrderStatus,
  Quote,
  useMembershipPoliciesQuery,
  useOrderQueryCallback,
  usePartnerCatalogQueryMaybe,
  useSpaceBookingPolicyQuery,
} from "./api";
import { BackButton } from "./back_button";
import { ContactFormProvider } from "./contact_form_provider";
import { Content } from "./content";
import { Divider } from "./divider";
import { Footer } from "./footer";
import { Hidden } from "./hidden";
import { LoadingOverlay } from "./loading_overlay";
import { MarketingConsent } from "./marketing_consent";
import { OfferingFooterMobile } from "./offering_footer_mobile";
import { OfferingHeader } from "./offering_header";
import { useSyncQueryString } from "./offering_helpers";
import { OrderParams } from "./order_params";
import { PageLayout } from "./page_layout";
import { PartnerConfig } from "./partner_config";
import { QuoteView } from "./quote_view";
import { Spacer } from "./spacer";
import {
  BillingDetails,
  OfferingParams,
  GuestDetails,
  PaymentCTA,
  PaymentDetails,
  PaymentCTAWithTerms,
} from "./template_offering_checkout_common";
import { useSubmitOrder } from "./template_offering_checkout_tranzila_base";
import { Text } from "./text";
import {
  useAppleGooglePayEmbedInCheckoutPage,
  useBillingDetailsUpdate,
  usePaymentModalFeatureFlag,
} from "../core/splitio";
import { useAnalytics } from "@/lib/analytics/analytics";
import {
  getEndDateFromOrderParams,
  getStartDateFromOrderParams,
} from "./order_params_utils";
import { MALIN_PARTNER_UUIDS } from "@/offering/themalin/themalin_partner_config";

interface TemplateOfferingCheckoutStripeBaseProps {
  partnerConfig: PartnerConfig;
  disabledCheckout: boolean;
  offeringName: string;
  partnerOfferingSlug: string;

  // Data
  includeQuantity: boolean | undefined;
  quote: Quote | undefined;
  space: Space | undefined;
  location: Location | undefined;
  partnerOffering: PartnerOffering;
  askForTax: boolean;
  askForBookingReason?: boolean;
  showTalkWithUsPrompt?: boolean;

  // Checkout
  orderParams: OrderParams;
  form: {
    values: CheckoutStripeInput;
    errors: FormikErrors<CheckoutStripeInput>;
    touched: FormikTouched<CheckoutStripeInput>;
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => any;
    setFieldError: (field: string, value: string | undefined) => void;
    submit: () => Promise<void>;
    submitting: boolean;
  };
  promotion: React.ReactNode;
  validateOrderParams: () => void;
  onPressBack: () => void;
  onChangeEmail: (email?: string) => void;
  mobileCheckoutBox: React.ReactNode;
  desktopCheckoutBox: React.ReactNode;
}

export function TemplateOfferingCheckoutStripeBase(
  props: TemplateOfferingCheckoutStripeBaseProps
) {
  const {
    partnerConfig,
    partnerOfferingSlug,
    offeringName,
    includeQuantity,
    quote,
    promotion,
    disabledCheckout,
    space,
    location,
    partnerOffering,
    askForTax,
    askForBookingReason,

    orderParams,
    form,
    validateOrderParams,
    onPressBack,

    showTalkWithUsPrompt,
    mobileCheckoutBox,
    desktopCheckoutBox,
    onChangeEmail,
  } = props;
  const {
    hubspotForm,
    favicon,
    termsURL,
    showLanguageSelector = false,
  } = partnerConfig;
  const {
    values,
    errors,
    setFieldValue,
    submit,
    submitting,
    setFieldError,
    touched,
  } = form;
  useSyncQueryString(orderParams);
  const queryString = useQueryString<OfferingParams>();
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryString.fromCatalog
  );
  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(space?.id);
  const quotePositivePrice = quote ? quote.totalPrice > 0 : false;

  const handleSubmit = useCallback(() => {
    validateOrderParams();
    submit();
  }, [validateOrderParams, submit]);

  return (
    <ContactFormProvider
      partnerID={partnerOffering.partner.id}
      messageableID={partnerOfferingSlug}
      messageableType={OfferingMessageType.PartnerOffering}
      hubspotForm={hubspotForm}
    >
      <Container>
        <Helmet>
          <title>
            {offeringName} - {partnerOffering.partner.name}
          </title>
          <link href={favicon} rel="shortcut icon" type="image/x-icon" />
        </Helmet>
        <OfferingHeader
          partnerID={partnerOffering.partner.id}
          offeringID={partnerOfferingSlug}
          homepageURL={partnerOffering.partner.homepageUrl}
          logo={partnerOffering.partner.logoUrl}
          hubspotForm={hubspotForm}
          showLanguageSelector={showLanguageSelector}
          localeOptions={partnerConfig.localeOptions}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
        />
        <Content>
          <Container paddingHorizontal={16}>
            <Spacer size={16} />
            <BackButton onPress={onPressBack} />
            <Spacer size={16} />
          </Container>
          <Spacer size={24} />
        </Content>
        <PageLayout
          content={
            <Container paddingHorizontal={16}>
              <GuestDetails
                onChangeEmail={onChangeEmail}
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                askForBookingReason={askForBookingReason}
              />
              {!partnerCatalog?.promoCode && (
                <Fragment>
                  {promotion}
                  <Spacer size={24} />
                </Fragment>
              )}
              <Hidden md>
                <Text weight="bold">{offeringName}</Text>
                <Spacer size={8} />
                <div className="template-offering-checkout-box-mobile">
                  {mobileCheckoutBox}
                </div>
                <Spacer size={16} />
                <Divider />
                {quote && space && location && (
                  <Fragment>
                    <QuoteView
                      orderParams={orderParams}
                      locationTimezone={location.timezone}
                      includeQuantity={includeQuantity}
                      currency={space.currencyCode}
                      pricingInclusion={location.pricingInclusion}
                      quote={quote}
                    />
                    <Spacer size={16} />
                  </Fragment>
                )}
              </Hidden>
              <Spacer size={24} />
              {(quote?.totalPrice === undefined || quote?.totalPrice > 0) && (
                <Fragment>
                  <BillingDetails
                    askForTax={askForTax}
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                  <Spacer size={24} />
                  <PaymentDetails
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                  />
                </Fragment>
              )}
              <Spacer size={24} />
              {spaceBookingPolicy?.marketingConsent && (
                <Container padding={16}>
                  <Spacer size={24} />
                  <MarketingConsent
                    marketingConsent={spaceBookingPolicy?.marketingConsent}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                </Container>
              )}
              {spaceBookingPolicy?.terms ? (
                <PaymentCTAWithTerms
                  isPaymentRequired={quotePositivePrice}
                  terms={spaceBookingPolicy?.terms}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                  onSubmit={handleSubmit}
                  disabled={!values.termsChecked || disabledCheckout}
                />
              ) : (
                <PaymentCTA
                  isPaymentRequired={quotePositivePrice}
                  termsURL={termsURL}
                  onSubmit={handleSubmit}
                  disabled={disabledCheckout}
                />
              )}
            </Container>
          }
          rightSidebar={
            <Container>
              <Text weight="bold">{offeringName}</Text>
              <Spacer size={8} />
              {desktopCheckoutBox}
            </Container>
          }
        />
        <Spacer size={24} />
        <OfferingFooterMobile
          showLanguageSelector={showLanguageSelector}
          hubspotForm={hubspotForm}
          partnerID={partnerOffering.partner.id}
          offeringID={partnerOfferingSlug}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          localeOptions={partnerConfig.localeOptions}
        />
        <Spacer size={20} />
        <Footer />
        {submitting && <LoadingOverlay />}
      </Container>
    </ContactFormProvider>
  );
}

export interface CheckoutStripeInput {
  fullName: string;
  companyName: string;
  phoneNumber: string;
  email: string;
  bookingReason?: string;
  paymentComplete: boolean;
  billingFullName?: string;
  billingPhoneNumber?: string;
  billingEmail?: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  billingTax: string;
  marketingConsent: boolean;
  termsChecked?: boolean;
  specialInstructions?: string;
  membershipNumber?: string;
}

interface UseCheckoutStripeProps {
  requireBillingDetails: boolean;
  requirePayment: boolean;
  askForTax: boolean;
  requireBookingReason?: boolean;
  stripe: Stripe | null;
  initialValues: CheckoutStripeInput;
  orderParams: OrderParams;
  partnerID: string;
  quoteID?: string;
  partnerOfferingID: string;
  offeringLocationId?: string;
  legacy__details: {
    layoutID: string;
    attendees: number;
  };
  onComplete?: (order: Order) => void;
  onError?: (msg: string) => void;
  onOrderCreated?: (order: Order) => void;
  useStripePaymentElement?: boolean;
  membership?: boolean;
  isEquinox?: boolean;
}

export function useCheckoutStripe(props: UseCheckoutStripeProps) {
  const {
    requirePayment,
    requireBillingDetails,
    askForTax,
    requireBookingReason,
    stripe,
    orderParams,
    partnerID,
    quoteID,
    partnerOfferingID,
    onComplete,
    onError,
    onOrderCreated,
    initialValues,
    legacy__details,
    membership,
    offeringLocationId,
    useStripePaymentElement = false,
  } = props;

  const queryParams = useQueryString();
  const isEquinox =
    queryParams.fromCatalog === "equinox" &&
    MALIN_PARTNER_UUIDS.includes(partnerID);
  const membershipNumberErrorRef = useRef<string | undefined>(undefined);
  const elements = useElements();
  const { fbt } = useI18n();
  const isGoogleApplePayEmbedInCheckoutPage =
    useAppleGooglePayEmbedInCheckoutPage(partnerID);
  const isBillingDetailsUpdateFlagEnabled = useBillingDetailsUpdate(partnerID);
  const paymentModalFeatureFlag = usePaymentModalFeatureFlag();
  const analytics = useAnalytics();
  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    orderParams.spaceID,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const { data: membershipPolicy } = useMembershipPoliciesQuery(
    offeringLocationId && membership ? offeringLocationId : undefined
  );

  const requireTerms =
    !!spaceBookingPolicy?.terms ||
    (!!membershipPolicy?.terms && membershipPolicy.termsRequired);
  const getOrder = useOrderQueryCallback();
  const completedRef = useRef(false);

  // errors in this callback are called in submitOrder and some from errors loading stripe
  const handleError = useCallback(
    (msg: string, error?: any, order?: Order | null) => {
      Sentry.withScope((scope) => {
        if (error) {
          scope.setExtras(error);
        }
        Sentry.captureMessage(`submit order error: ${msg}`, "error");
      });

      const replay = Sentry.getReplay();
      replay?.start();
      // This is a duplicate event to above, but should make it easier to track errors on Mixpanel
      // Depending on usage, we may want to remove the above event
      analytics.event(
        "Checkout API Error",
        {
          "Error Message": msg,
          "API Error": error,
          "Order Params": orderParams,
          "Quote ID": quoteID,
          "Order ID": order?.id,
          "Require Payment": requirePayment,
          "Require Billing Details": requireBillingDetails,
          "Require Tax": askForTax,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );

      if (onError) {
        onError(msg);
      }
    },
    [
      analytics,
      onError,
      orderParams,
      quoteID,
      requirePayment,
      requireBillingDetails,
      askForTax,
    ]
  );

  const submitOrder = useSubmitOrder({
    partnerID,
    quoteID,
    partnerOfferingID,
    requirePayment,
    onError: handleError,
  });

  const [areContactDetailsSameAsBilling, setAreContactDetailsSameAsBilling] =
    useState(false);

  const companyNameRequired = askForTax && !!(requirePayment || membership);

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    submitForm,
    isSubmitting,
    setFieldTouched,
  } = useFormik<CheckoutStripeInput>({
    initialValues,
    validate: (v) =>
      getCheckoutErrors(
        v,
        requireBillingDetails,
        !areContactDetailsSameAsBilling &&
          askForTax &&
          isBillingDetailsUpdateFlagEnabled,
        askForTax,
        requirePayment &&
          isGoogleApplePayEmbedInCheckoutPage &&
          !paymentModalFeatureFlag,
        requireTerms,
        companyNameRequired,
        fbt,
        requireBookingReason,
        isEquinox,
        membershipNumberErrorRef.current
      ),
    onSubmit: async (v) => {
      if (!useStripePaymentElement && (!stripe || !elements)) {
        handleError(fbt("Stripe was not loaded"));
        return;
      }

      const values = { ...v };

      if (
        askForTax &&
        areContactDetailsSameAsBilling &&
        isBillingDetailsUpdateFlagEnabled
      ) {
        values.billingFullName = values.fullName;
        values.billingPhoneNumber = values.phoneNumber;
        values.billingEmail = values.email;
      }

      if (!isEquinox) {
        values.membershipNumber = undefined;
      }

      const order = await submitOrder(values, orderParams, legacy__details);

      // errors are already handled in submitOrder
      if (!order) {
        return;
      }

      if (!requirePayment) {
        if (useStripePaymentElement && !!onOrderCreated && membership) {
          onOrderCreated(order);
          return;
        }

        if (onComplete) {
          onComplete(order);
        }
        return;
      }

      if (useStripePaymentElement && onOrderCreated) {
        onOrderCreated(order);
        return;
      }

      if (!stripe || !elements) {
        handleError(fbt("Stripe was not loaded"));
        return;
      }

      const card = elements.getElement(CardElement);

      if (!card) {
        handleError(fbt("Card component not found"));
        return;
      }

      const result = await stripe.confirmCardPayment(order.stripeClientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: values.fullName,
          },
        },
      });

      if (result.error) {
        handleError(
          result.error.message || "Payment unsuccessful",
          result.error
        );
      } else if (result.paymentIntent) {
        // The payment has been processed!
        if (result.paymentIntent.status === "succeeded") {
          await new Promise<void>((resolve) => {
            let count = 0;

            // Start polling
            const interval = setInterval(async () => {
              // poll for maximum 60 seconds
              if (order && count < 60) {
                const polledOrder = await getOrder(order.id);
                count++;
                if (
                  polledOrder.status === OrderStatus.Paid &&
                  polledOrder.orderItems.length > 0
                ) {
                  clearInterval(interval);
                  // Even though we clear interval, async nature may still call this function
                  // multiple times as response is returned
                  if (onComplete && completedRef.current === false) {
                    completedRef.current = true;
                    onComplete(order);
                  }
                  resolve();
                }
              } else {
                clearInterval(interval);
                handleError(fbt("Payment unsuccessful"), { order });
                resolve();
              }
            }, 1000);
          });
        }
      }
    },
  });

  membershipNumberErrorRef.current = errors.membershipNumber;

  return {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    submit: submitForm,
    submitting: isSubmitting,
    onCheckContactDetailsSameAsBilling: setAreContactDetailsSameAsBilling,
  };
}

export function getCheckoutErrors(
  values: CheckoutStripeInput,
  requireBillingDetails: boolean,
  requireBillingContactDetails: boolean,
  askForTax: boolean,
  requirePayment: boolean,
  requireTerms: boolean,
  companyNameRequired: boolean,
  fbt: FBT,
  requireBookingReason?: boolean,
  isEquinox?: boolean,
  currentMembershipNumberError?: string
) {
  const emptyMembershipNumberError = fbt("Please add your membership number");
  const invalidMembershipNumberLengthError = fbt(
    "Please enter only ten digits"
  );
  const errors: { [field: string]: string } = {};

  if (requirePayment && !values.paymentComplete) {
    errors.paymentComplete = fbt("Please enter credit card info");
  }

  if (requireTerms && !values.termsChecked) {
    errors.termsChecked = fbt("Please agree to the terms and conditions");
  }

  if (!values.fullName) {
    errors.fullName = fbt("Please add your full name");
  }

  if (companyNameRequired && !values.companyName.trim()) {
    errors.companyName = fbt("Please add your company name");
  }

  if (!values.phoneNumber?.trim()) {
    errors.phoneNumber = fbt("Please add your phone number");
  } else if (values.phoneNumber.length < 5) {
    errors.phoneNumber = fbt("Please enter valid phone number");
  }

  if (!values.email) {
    errors.email = fbt("Please add your email");
  } else if (!isEmail(values.email)) {
    errors.email = fbt("Please enter valid email");
  }

  if (isEquinox && !values.membershipNumber) {
    errors.membershipNumber = emptyMembershipNumberError;
  } else if (
    isEquinox &&
    values.membershipNumber &&
    !isTenDigits(values.membershipNumber)
  ) {
    errors.membershipNumber = invalidMembershipNumberLengthError;
  } else if (
    isEquinox &&
    currentMembershipNumberError &&
    currentMembershipNumberError !== emptyMembershipNumberError &&
    currentMembershipNumberError !== invalidMembershipNumberLengthError
  ) {
    errors.membershipNumber = currentMembershipNumberError;
  }

  if (requireBillingDetails) {
    if (requireBillingContactDetails) {
      if (!values.billingFullName?.trim()) {
        errors.billingFullName = fbt("Please add your full name");
      }
      if (!values.billingPhoneNumber?.trim()) {
        errors.billingPhoneNumber = fbt("Please add your phone number");
      } else if (values.billingPhoneNumber.length < 5) {
        errors.billingPhoneNumber = fbt("Please enter valid phone number");
      }
      if (!values.billingEmail?.trim()) {
        errors.billingEmail = fbt("Please add your email");
      } else if (!isEmail(values.billingEmail)) {
        errors.billingEmail = fbt("Please enter valid email");
      }
    }
    if (!values.billingStreetAddress) {
      errors.billingStreetAddress = fbt("Please add billing street address");
    }
    if (!values.billingCity) {
      errors.billingCity = fbt("Please add billing city");
    }
    if (!values.billingPostalCode) {
      errors.billingPostalCode = fbt("Please add postal code");
    }
    if (!values.billingCountry) {
      errors.billingCountry = fbt("Please add billing country");
    }
  }

  if (askForTax) {
    if (!values.billingTax) {
      errors.billingTax = fbt("Please add tax information");
    } else if (
      isCountryIsrael(values.billingCountry) &&
      !isValidIsraeliTaxID(values.billingTax)
    ) {
      errors.billingTax = fbt("Invalid Israel tax id");
    }
  }

  if (requireBookingReason && !values.bookingReason) {
    errors.bookingReason = fbt("missingBookingReasonError");
  }

  return errors;
}
